import { BannerPosition } from 'features/banner/model/banner';
import { UpsellKey } from 'features/google-tag-manager/enum/upsell-key';
import useCurrentSubscriptionHelper from 'hooks/billing/useCurrentSubscriptionHelper';
import { useUpgradePlan } from 'hooks/billing/useUpgradePlan';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UpsellCardBaseProps, UpsellCardSpotlightProps } from '../../upsell-card/models/upsell-card-props';
import { UsageStatus } from '../models/usage-status';
import { useShowFreeTrial } from './useShowFreeTrial';

/**
 * Spotlight upsell card props for the given monthly order usage status.
 */
export const useUsageUpsellCard = (status: UsageStatus): UpsellCardBaseProps & UpsellCardSpotlightProps => {
    const { t } = useTranslation();
    const { goToPlan } = useCurrentSubscriptionHelper();
    const { name: upgradePlanName, handle: upgradePlanHandle, trialDays } = useUpgradePlan();
    // The trial wording also needs the plan's trial length, so it isn't shown as "0 days" (plans still
    // loading, or a plan without a trial)
    const showFreeTrial = useShowFreeTrial() && trialDays > 0;

    return useMemo((): UpsellCardBaseProps & UpsellCardSpotlightProps => {
        // Straight to the upgrade plan's own page (monthly billing), reporting which card was clicked
        const action = (upsellKey: UpsellKey) => ({ onClick: () => goToPlan(upgradePlanHandle, { upsellKey }) });
        const subscribeLabel = t('DASHBOARD.UPSELL_CARD.UPGRADE_PLAN', { plan: upgradePlanName });

        switch (status) {
            case UsageStatus.LIMIT_REACHED:
                return {
                    variant: 'spotlight',
                    theme: 'lavender',
                    badge: t('DASHBOARD.UPSELL_CARD.LIMIT_REACHED.BADGE'),
                    title: t('DASHBOARD.UPSELL_CARD.LIMIT_REACHED.TITLE'),
                    description: t('DASHBOARD.UPSELL_CARD.LIMIT_REACHED.DESCRIPTION'),
                    action: {
                        ...action(UpsellKey.USAGE_LIMIT_REACHED),
                        label: showFreeTrial
                            ? t('DASHBOARD.UPSELL_CARD.LIMIT_REACHED.ACTION', {
                                  count: trialDays,
                                  plan: upgradePlanName,
                              })
                            : subscribeLabel,
                    },
                    position: BannerPosition.DASHBOARD_PAGE,
                };
            case UsageStatus.ALMOST_FULL:
                return {
                    variant: 'spotlight',
                    theme: 'mint',
                    eyebrow: t('DASHBOARD.UPSELL_CARD.ALMOST_FULL.EYEBROW'),
                    title: t('DASHBOARD.UPSELL_CARD.ALMOST_FULL.TITLE'),
                    description: t('DASHBOARD.UPSELL_CARD.ALMOST_FULL.DESCRIPTION'),
                    action: {
                        ...action(UpsellKey.USAGE_ALMOST_FULL),
                        label: showFreeTrial
                            ? t('DASHBOARD.UPSELL_CARD.ALMOST_FULL.ACTION', { count: trialDays })
                            : subscribeLabel,
                    },
                    position: BannerPosition.DASHBOARD_PAGE,
                };
            case UsageStatus.RUNNING_LOW:
                return {
                    variant: 'spotlight',
                    theme: 'dark',
                    eyebrow: t('DASHBOARD.UPSELL_CARD.RUNNING_LOW.EYEBROW'),
                    title: t('DASHBOARD.UPSELL_CARD.RUNNING_LOW.TITLE'),
                    description: t('DASHBOARD.UPSELL_CARD.RUNNING_LOW.DESCRIPTION'),
                    action: {
                        ...action(UpsellKey.USAGE_RUNNING_LOW),
                        label: showFreeTrial
                            ? t('DASHBOARD.UPSELL_CARD.RUNNING_LOW.ACTION', { count: trialDays })
                            : subscribeLabel,
                    },
                    position: BannerPosition.DASHBOARD_PAGE,
                };
            default:
                return {
                    variant: 'spotlight',
                    theme: 'lime',
                    badge: t('DASHBOARD.UPSELL_CARD.ON_TRACK.BADGE', { plan: upgradePlanName }),
                    title: t('DASHBOARD.UPSELL_CARD.ON_TRACK.TITLE'),
                    description: t('DASHBOARD.UPSELL_CARD.ON_TRACK.DESCRIPTION'),
                    action: {
                        ...action(UpsellKey.USAGE_ON_TRACK),
                        label: showFreeTrial
                            ? t('DASHBOARD.UPSELL_CARD.ON_TRACK.ACTION', { count: trialDays })
                            : subscribeLabel,
                    },
                    position: BannerPosition.DASHBOARD_PAGE,
                };
        }
    }, [status, showFreeTrial, trialDays, upgradePlanName, upgradePlanHandle, goToPlan, t]);
};
